/* ===========================================================================
   Size units

   A pair is one physical size, so a card shows it once. The markup carries the
   canonical US size and nothing else; this file renders it in whichever unit
   the shopper picked. Switching the unit repaints what is already on screen -
   no refetch, and a tile never carries four spellings of the same size.

   The rows are the exact Crocodilewear lookup tables, ported from
   Inventory\server\services\size-converter.js - a chart, not a formula. Each
   row is [US, EU, CM, MM], so a size located in one column converts by reading
   across.

   Loaded before browse.js. Also require()-able, so dev/shopify-context.js
   normalises incoming sizes through this same chart instead of a second copy.
   =========================================================================== */

(function (root) {
  'use strict';

  var MENS = [
    [4, 36, 22.0, 220], [4.5, 37, 22.5, 225], [5, 37.5, 23.0, 230], [5.5, 38, 23.5, 235],
    [6, 38.5, 24.0, 240], [6.5, 39.5, 24.5, 245], [7, 40, 25.0, 250], [7.5, 40.5, 25.5, 255],
    [8, 41.5, 26.0, 260], [8.5, 42, 26.5, 265], [9, 42.5, 27.0, 270], [9.5, 43, 27.5, 275],
    [10, 44, 28.0, 280], [10.5, 44.5, 28.5, 285], [11, 45, 29.0, 290], [11.5, 45.5, 29.5, 295],
    [12, 46.5, 30.0, 300], [12.5, 47, 30.5, 305], [13, 47.5, 31.0, 310], [14, 49, 32.0, 320],
    [15, 50, 33.0, 330], [16, 51, 34.0, 340]
  ];

  var WOMENS = [
    [4, 34, 21.0, 210], [4.5, 34.5, 21.5, 215], [5, 35, 22.0, 220], [5.5, 36, 22.5, 225],
    [6, 36.5, 23.0, 230], [6.5, 37, 23.5, 235], [7, 37.5, 24.0, 240], [7.5, 38, 24.5, 245],
    [8, 39, 25.0, 250], [8.5, 40, 25.5, 255], [9, 40.5, 26.0, 260], [9.5, 41, 26.5, 265],
    [10, 41.5, 27.0, 270], [10.5, 42.5, 27.5, 275], [11, 43, 28.0, 280], [11.5, 43.5, 28.5, 285],
    [12, 44, 29.0, 290], [12.5, 45, 29.5, 295], [13, 45.5, 30.0, 300], [13.5, 46.5, 30.5, 305],
    [14, 47, 31.0, 310], [15, 47.5, 32.0, 320]
  ];

  /* The chart has four columns; the storefront offers two of them. CM and MM
     stay in the chart because stock is recorded in them - a pair logged as
     MM 270 still has to normalise to US 9 - they are just not something a
     shopper picks between. */
  var COLUMN = { US: 0, EU: 1, CM: 2, MM: 3 };
  var UNITS = ['US', 'EU'];

  /* Inventory spells the European unit EURO; the storefront shows EU. */
  var ALIASES = { US: 'US', EU: 'EU', EUR: 'EU', EURO: 'EU', CM: 'CM', MM: 'MM' };

  var STORAGE_KEY = 'browse.sizeUnit';
  var DEFAULT_UNIT = 'US';

  // -------------------------------------------------------------------------
  // The chart
  // -------------------------------------------------------------------------

  function normaliseUnit(unit) {
    return ALIASES[String(unit || '').toUpperCase()] || null;
  }

  /* Only a unit the switcher actually offers can be the current one, so a
     stale stored value cannot strand the page in a unit with no way back. */
  function offeredUnit(unit) {
    var found = normaliseUnit(unit);
    return found && UNITS.indexOf(found) !== -1 ? found : null;
  }

  /* A size with no unit written on it - a bare `size-38` tag, or a plain "38"
     from a variant title like "38 / Black". All four chart columns are read,
     not just the two the switcher offers, because stock is recorded in all of
     them and a pair logged as 270 still has to normalise to US 9:

       >= 200   MM    210 - 340
       >= 34    EU     34 - 51
       >= 17    CM     21 - 34
       else     US      4 - 16

     The ranges only touch at 34, where EU wins: EU 34 is a real women's size
     and CM 34 is a 13-inch foot. */
  function inferUnit(value) {
    var number = parseFloat(value);
    if (isNaN(number) || number <= 0) return null;
    if (number >= 200) return 'MM';
    if (number >= 34) return 'EU';
    if (number >= 17) return 'CM';
    return 'US';
  }

  /** Men's and women's charts are different tables, not an offset. */
  function tableFor(category) {
    return String(category || '').toLowerCase().charAt(0) === 'w' ? WOMENS : MENS;
  }

  function format(number, unit) {
    if (unit === 'CM') return number.toFixed(1);
    if (unit === 'MM') return String(Math.round(number));
    return number % 1 === 0 ? String(number) : number.toFixed(1);
  }

  /* How far a recorded size may sit from a chart row and still count as that
     row: a whole size in US or EU, half a centimetre, five millimetres. */
  function tolerance(column) {
    if (column <= COLUMN.EU) return 1;
    return column === COLUMN.CM ? 0.5 : 5;
  }

  /** Nearest row within tolerance - how an incoming size finds its chart row. */
  function nearestRow(column, value, category) {
    var table = tableFor(category);
    var number = parseFloat(value);
    if (isNaN(number)) return null;

    var best = null;
    var bestDiff = Infinity;
    for (var i = 0; i < table.length; i += 1) {
      var diff = Math.abs(table[i][column] - number);
      if (diff === 0) return table[i];
      if (diff < bestDiff) {
        bestDiff = diff;
        best = table[i];
      }
    }
    return bestDiff <= tolerance(column) ? best : null;
  }

  /* Converting *out of* US is exact-match only. A half size that is not on the
     chart stays in US rather than being rounded onto a neighbouring row and
     shown as a European size the pair is not. */
  function usRow(us, category) {
    var table = tableFor(category);
    var number = parseFloat(us);
    if (isNaN(number)) return null;
    for (var i = 0; i < table.length; i += 1) {
      if (table[i][COLUMN.US] === number) return table[i];
    }
    return null;
  }

  // -------------------------------------------------------------------------
  // Conversion
  // -------------------------------------------------------------------------

  /**
   * Any recorded size -> the US size it maps to, as a string.
   * Null when it is off the chart entirely.
   */
  function toUS(size, unit, category) {
    var from = normaliseUnit(unit);
    if (!from || !size) return null;

    if (from === 'US') {
      var direct = parseFloat(size);
      return isNaN(direct) ? null : format(direct, 'US');
    }

    var row = nearestRow(COLUMN[from], size, category);
    return row ? format(row[COLUMN.US], 'US') : null;
  }

  /** A US size read across into another unit: ('5.5', 'women', 'EU') -> '36'. */
  function convert(us, category, unit) {
    var to = normaliseUnit(unit);
    if (!to) return null;

    if (to === 'US') {
      var direct = parseFloat(us);
      return isNaN(direct) ? null : format(direct, 'US');
    }

    var row = usRow(us, category);
    return row ? format(row[COLUMN[to]], to) : null;
  }

  /** The labelled size, e.g. "EU 36". Null when this pair is off that chart. */
  function label(us, category, unit) {
    var value = convert(us, category, unit);
    return value ? normaliseUnit(unit) + ' ' + value : null;
  }

  /**
   * The same, but the size as it was actually recorded wins for its own unit.
   *
   * The chart skips sizes - there is no 46 in the men's European column - so a
   * pair labelled EU 46 normalises to US 11.5 and would read back as EU 45.5.
   * A shopper looking at European sizes should see the size on the shoe, so
   * convert only when there is something to convert.
   */
  function labelAs(us, category, unit, recorded) {
    var to = normaliseUnit(unit);
    if (recorded && to && recorded.unit === to && recorded.value) {
      var number = parseFloat(recorded.value);
      if (!isNaN(number)) return to + ' ' + format(number, to);
    }
    return label(us, category, to);
  }

  /**
   * The whole row: "US 5.5 · EU 36 · CM 22.5 · MM 225".
   * The other units are not on the card - they are one hover away.
   */
  function crossReference(us, category, recorded) {
    var parts = [];
    for (var i = 0; i < UNITS.length; i += 1) {
      var text = labelAs(us, category, UNITS[i], recorded);
      if (text) parts.push(text);
    }
    return parts.length > 1 ? parts.join(' · ') : '';
  }

  // -------------------------------------------------------------------------
  // The shopper's choice
  // -------------------------------------------------------------------------

  var current = DEFAULT_UNIT;

  function get() {
    return current;
  }

  function set(unit) {
    var next = offeredUnit(unit);
    if (!next || next === current) return;
    current = next;
    try {
      root.localStorage.setItem(STORAGE_KEY, next);
    } catch (error) {
      /* private mode / quota - the choice just won't outlive the page */
    }
    syncControls();
    apply(root.document);
  }

  function restore() {
    var saved = null;
    try {
      saved = root.localStorage.getItem(STORAGE_KEY);
    } catch (error) {}
    var unit = offeredUnit(saved);
    if (unit) current = unit;
  }

  // -------------------------------------------------------------------------
  // Painting
  // -------------------------------------------------------------------------

  /**
   * Render every size inside `scope` in the current unit. Safe to call on
   * anything just swapped in through the Section Rendering API.
   */
  function apply(scope) {
    if (!scope || !scope.querySelectorAll) return;
    var nodes = scope.querySelectorAll('[data-size]');
    for (var i = 0; i < nodes.length; i += 1) paint(nodes[i]);
  }

  /* `data-size-native` is the size exactly as recorded. It is what shows when a
     pair cannot be converted, so a size never disappears off a card just
     because it is not on the chart. */
  function paint(node) {
    var us = node.getAttribute('data-size-us');
    var category = node.getAttribute('data-size-category');
    var native = node.getAttribute('data-size-native') || '';
    var recorded = {
      unit: normaliseUnit(node.getAttribute('data-size-native-unit')),
      value: node.getAttribute('data-size-native-value')
    };

    /* The canonical US size is what every conversion reads across from, so
       without it the switcher has nothing to switch and the card would sit on
       its native size in both units. Most stores have no `custom.us_size`
       metafield, so derive it from whatever the size *was* recorded as. */
    if (!recorded.unit && recorded.value) recorded.unit = inferUnit(recorded.value);
    if (!us && recorded.unit && recorded.value) {
      us = toUS(recorded.value, recorded.unit, category);
    }

    node.textContent = labelAs(us, category, current, recorded) || native;

    var full = crossReference(us, category, recorded);
    if (full || native) node.setAttribute('title', full || native);
  }

  function syncControls() {
    var buttons = root.document.querySelectorAll('[data-size-unit]');
    for (var i = 0; i < buttons.length; i += 1) {
      var active = buttons[i].getAttribute('data-size-unit') === current;
      buttons[i].classList.toggle('is-active', active);
      buttons[i].setAttribute('aria-pressed', active ? 'true' : 'false');
    }
  }

  function init() {
    restore();

    /* Document-scoped: the switcher renders once per deck toolbar, and both
       copies stay in step because neither one owns the choice. */
    root.document.addEventListener('click', function (event) {
      var target = event.target;
      var button = target && target.closest ? target.closest('[data-size-unit]') : null;
      if (!button) return;
      event.preventDefault();
      set(button.getAttribute('data-size-unit'));
    });

    syncControls();
    apply(root.document);
  }

  // -------------------------------------------------------------------------

  var SizeUnits = {
    UNITS: UNITS,
    DEFAULT_UNIT: DEFAULT_UNIT,
    toUS: toUS,
    normaliseUnit: normaliseUnit,
    inferUnit: inferUnit,
    convert: convert,
    label: label,
    labelAs: labelAs,
    crossReference: crossReference,
    get: get,
    set: set,
    apply: apply
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = SizeUnits;

  if (root && root.document) {
    root.SizeUnits = SizeUnits;
    if (root.document.readyState === 'loading') {
      root.document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  }
})(typeof window !== 'undefined' ? window : null);
